import { memo, useEffect, useRef } from 'react'
import { useMap, useMapEvents } from 'react-leaflet'
import { getBoundsRadius } from '../../utils/mapUtils'
import { createLogger } from '../../utils/logger'

const logger = createLogger('MapBoundsListener')

// How long after the user's LAST scroll notch to fire the radius update.
// Scheduled from zoomstart (not zoomend) so every notch resets the countdown.
// 1000ms means: "fire 1s after the user stops scrolling the wheel".
const ZOOM_IDLE_MS = 1000
// Pan: short debounce after moveend.
const PAN_DEBOUNCE_MS = 400

/**
 * MapBoundsListener - Null-render Leaflet event listener for dynamic entity fetching
 *
 * KEY DESIGN: zoom and pan are treated differently.
 *
 *   ZOOM → calls onRadiusChange(radiusKm) only (does NOT update stored location).
 *     Timer is reset on each zoomstart so the fetch fires ZOOM_IDLE_MS after the
 *     user's LAST scroll notch — not after each animation completes. This correctly
 *     coalesces both fast and slow scroll-wheel zooming into a single call.
 *
 *   PAN → calls onBoundsChange(map) (updates location + radius).
 *     Simple 400ms debounce after moveend.
 *
 * Must be rendered inside a <MapContainer>.
 * @atomic @idempotent
 */
const MapBoundsListener = memo(function MapBoundsListener({
  onBoundsChange,
  onRadiusChange,
  disabled = false
}) {
  const map = useMap()
  const timerRef = useRef(null)
  const isZoomingRef = useRef(false)

  // Sync radius immediately on mount; clear pending timers on unmount.
  useEffect(() => {
    if (!disabled) {
      const radiusKm = getBoundsRadius(map)
      if (onRadiusChange) onRadiusChange(radiusKm)
      else onBoundsChange(map)
    }
    return () => clearTimeout(timerRef.current)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Returns true if the map container is mounted and has its pane positioned.
  // Guards against '_leaflet_pos' TypeError when the map unmounts mid-animation.
  const mapReady = () => {
    try { map.getCenter(); return true } catch { return false }
  }

  useMapEvents({
    // Every scroll notch: reset the idle countdown from NOW.
    // Previous timer (from the prior notch) is cancelled before it can fire.
    zoomstart: () => {
      if (disabled) return
      isZoomingRef.current = true
      clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        if (!mapReady()) return
        isZoomingRef.current = false
        const suppress = map._suppressBoundsUntil && Date.now() < map._suppressBoundsUntil
        if (suppress) {
          // A previous fetch is still in flight. Extend the suppress windows so
          // late-arriving entities don't trigger a fitBounds zoom-out after they
          // land. Then schedule a deferred fetch for the current zoom level so
          // the user still gets fresh entities once the window clears.
          const extendTo = Date.now() + 2500
          map._suppressBoundsUntil = extendTo
          map._skipFitBoundsUntil = extendTo
          timerRef.current = setTimeout(() => {
            if (map._suppressBoundsUntil && Date.now() < map._suppressBoundsUntil) return
            const radiusKm = getBoundsRadius(map)
            if (radiusKm == null) return
            map._suppressBoundsUntil = Date.now() + 2500
            map._skipFitBoundsUntil = Date.now() + 2500
            logger.info('Bounds changed (deferred zoom)', { center: map.getCenter(), zoom: map.getZoom(), radiusKm })
            if (onRadiusChange) onRadiusChange(radiusKm)
            else onBoundsChange(map)
          }, 2500)
          return
        }
        const radiusKm = getBoundsRadius(map)
        if (radiusKm == null) return
        // Suppress any moveend (MBL) and fitBounds (MapBoundsFitter/EntityMarkers)
        // triggered when entities arrive after a zoom-triggered fetch.
        const suppressUntil = Date.now() + 2500
        map._suppressBoundsUntil = suppressUntil
        map._skipFitBoundsUntil = suppressUntil
        logger.info('Bounds changed', { center: map.getCenter(), zoom: map.getZoom(), radiusKm })
        if (onRadiusChange) onRadiusChange(radiusKm)
        else onBoundsChange(map)
      }, ZOOM_IDLE_MS)
    },
    // zoomend just clears the flag — the timer is already running from zoomstart.
    zoomend: () => {
      if (disabled) return
    },
    // Pan: full location + radius update. Skip while zoom idle window is active.
    moveend: () => {
      if (disabled) return
      const suppress = map._suppressBoundsUntil && Date.now() < map._suppressBoundsUntil
      if (isZoomingRef.current) return
      if (suppress) return
      clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        if (!mapReady()) return
        logger.info('Bounds changed', {
          center: map.getCenter(),
          zoom: map.getZoom(),
          radiusKm: getBoundsRadius(map)
        })
        onBoundsChange(map)
      }, PAN_DEBOUNCE_MS)
    }
  })

  return null
})

export default MapBoundsListener
